import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import {
    createCompatType, deleteCompatLink, deleteCompatType, downloadCompatTemplate, importCompat, loadCompatLinks,
    loadCompatMeta, loadKbGroups, loadKnowledge, saveCompatLink,
} from '../../../../services/feedback';
import { errorText } from './feedbackUi';

const PAGE = 50;
const NEW_TYPE = '__new';
const EMPTY_SIDE = { scope: 'sku', items: [] };

function SidePicker({ accountId, title, value, onChange, scopes, multi }) {
    const { request } = useApi();
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const picked = value.items;
    const full = !multi && picked.length > 0;

    useEffect(() => {
        if (full) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                if (value.scope === 'sku') {
                    const data = await loadKnowledge({ request, accountId, query: query.trim(), limit: 8 });
                    if (!cancelled) setOptions(data.items.map((row) => ({ key: row.sku, label: `${row.offer_id || row.sku} · ${row.name || ''}`, stock: row.stock })));
                } else {
                    const data = await loadKbGroups({ request, accountId, scope: value.scope, query: query.trim(), limit: 8 });
                    if (!cancelled) setOptions(data.items.filter((row) => row.key).map((row) => ({ key: row.key, label: row.label, stock: row.stock })));
                }
            } catch (error) {
                if (!cancelled) setOptions([]);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [request, accountId, value.scope, full, query]);

    const add = (option) => {
        if (picked.some((item) => item.key === option.key)) return;
        const next = { key: option.key, label: option.label };
        onChange({ scope: value.scope, items: multi ? [...picked, next] : [next] });
        if (!multi) setQuery('');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div className="fb-muted">{title}</div>
            <select className="fb-input" value={value.scope} onChange={(event) => { setQuery(''); onChange({ scope: event.target.value, items: [] }); }}>
                {scopes.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
            {picked.length > 0 && (
                <div className="fb-row" style={{ flexWrap: 'wrap' }}>
                    {picked.map((item) => (
                        <span key={item.key} className="fb-chip fb-chip--good" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.label || item.key}{' '}
                            <button type="button" aria-label="Убрать" onClick={() => onChange({ scope: value.scope, items: picked.filter((other) => other.key !== item.key) })}>✕</button>
                        </span>
                    ))}
                </div>
            )}
            {!full && (
                <>
                    <input className="fb-input" placeholder={value.scope === 'sku' ? 'Название, артикул или SKU' : 'Начните вводить название'} value={query} onChange={(event) => setQuery(event.target.value)} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {options.map((option) => (
                            <button key={option.key} type="button" style={{ textAlign: 'left' }}
                                disabled={picked.some((item) => item.key === option.key)} onClick={() => add(option)}>
                                {option.label}{option.stock != null ? ` · остаток ${option.stock}` : ''}
                            </button>
                        ))}
                        {options.length === 0 && <span className="fb-muted">Ничего не найдено</span>}
                    </div>
                </>
            )}
        </div>
    );
}

function LinkForm({ accountId, meta, initial, onSaved, onCancel, onMetaChanged }) {
    const { request } = useApi();
    const [from, setFrom] = useState(initial?.from || EMPTY_SIDE);
    const [to, setTo] = useState(initial?.to || EMPTY_SIDE);
    const multi = !initial;   // новая форма создаёт связки на все выбранные пары, правка меняет одну
    const [type, setType] = useState(initial?.type || 'analog');
    const [both, setBoth] = useState(initial ? initial.bidirectional : true);
    const [note, setNote] = useState(initial?.note || '');
    const [custom, setCustom] = useState({ name: '', description: '' });
    const [message, setMessage] = useState('');

    const chooseType = (value) => {
        setType(value);
        const info = meta.types.find((item) => item.key === value);
        if (info) setBoth(info.bidirectional);
    };
    const addType = async () => {
        try {
            const created = await createCompatType({ request, accountId, name: custom.name, description: custom.description });
            setCustom({ name: '', description: '' });
            await onMetaChanged();
            setType(created.key);
            setBoth(true);
            setMessage('');
        } catch (error) {
            setMessage(errorText(error));
        }
    };
    const save = async () => {
        if (!from.items.length || !to.items.length) {
            setMessage('Выберите обе стороны связки');
            return;
        }
        try {
            await saveCompatLink({
                request, accountId, id: initial?.id,
                link: {
                    from_scope: from.scope, from_keys: from.items.map((item) => item.key),
                    to_scope: to.scope, to_keys: to.items.map((item) => item.key), type, bidirectional: both, note,
                },
            });
            onSaved();
        } catch (error) {
            setMessage(errorText(error));
        }
    };
    const info = meta.types.find((item) => item.key === type);

    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-row fb-spread">
                <h4 style={{ margin: 0 }}>{initial ? 'Связка' : 'Новая связка'}</h4>
                <button type="button" onClick={onCancel}>Закрыть</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                <SidePicker accountId={accountId} title="Откуда (о чём спрашивают)" value={from} onChange={setFrom} scopes={meta.scopes} multi={multi} />
                <SidePicker accountId={accountId} title="Куда (что предлагать)" value={to} onChange={setTo} scopes={meta.scopes} multi={multi} />
            </div>
            <div className="fb-row" style={{ flexWrap: 'wrap' }}>
                <select className="fb-input" value={type} onChange={(event) => chooseType(event.target.value)}>
                    {meta.types.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                    <option value={NEW_TYPE}>＋ Свой тип…</option>
                </select>
                {type !== NEW_TYPE && (
                    <label className="fb-note"><input type="checkbox" checked={both} onChange={(event) => setBoth(event.target.checked)} /> В обе стороны</label>
                )}
                {info && type !== NEW_TYPE && <span className="fb-muted">{info.hint}</span>}
            </div>
            {type === NEW_TYPE && (
                <div className="fb-faq__row" style={{ gridTemplateColumns: '1fr 2fr auto' }}>
                    <input className="fb-input" placeholder="Название типа" maxLength={60} value={custom.name} onChange={(event) => setCustom({ ...custom, name: event.target.value })} />
                    <input className="fb-input" placeholder="Что это значит (для ИИ), одной фразой" maxLength={300} value={custom.description} onChange={(event) => setCustom({ ...custom, description: event.target.value })} />
                    <button type="button" disabled={!custom.name.trim() || !custom.description.trim()} onClick={addType}>Создать</button>
                </div>
            )}
            <input className="fb-input" placeholder="Заметка для ИИ (необязательно): например, «только для версий 2023 года»" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
            <div className="fb-actions">
                <button type="button" className="primary" disabled={type === NEW_TYPE} onClick={save}>Сохранить</button>
                {message && <span className="fb-note">{message}</span>}
            </div>
        </div>
    );
}

function SideCell({ side }) {
    return (
        <div style={{ maxWidth: 320 }}>
            <div>{side.label}</div>
            <div className="fb-muted" style={{ fontSize: 12 }}>
                {side.scope === 'sku' ? `Артикул ${side.offer_id || '—'} · SKU ${side.key}` : `${side.scope === 'model' ? 'Модель' : side.scope === 'brand' ? 'Бренд' : 'Категория'} · товаров ${side.products}`}
            </div>
        </div>
    );
}

export default function FeedbackCompat({ accountId }) {
    const { request, fetchBlob } = useApi();
    const [meta, setMeta] = useState({ scopes: [], types: [] });
    const [query, setQuery] = useState('');
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState({ total: 0, items: [] });
    const [form, setForm] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const fileRef = useRef(null);

    const reloadMeta = useCallback(async () => {
        try {
            setMeta(await loadCompatMeta({ request, accountId }));
        } catch (err) {
            setError(errorText(err));
        }
    }, [request, accountId]);
    const reload = useCallback(async () => {
        setError('');
        try {
            setData(await loadCompatLinks({ request, accountId, query: query.trim(), limit: PAGE, offset }));
        } catch (err) {
            setError(errorText(err));
        }
    }, [request, accountId, query, offset]);

    useEffect(() => { reloadMeta(); }, [reloadMeta]);
    useEffect(() => { reload(); }, [reload]);

    const remove = async (id) => {
        try {
            await deleteCompatLink({ request, accountId, id });
            reload();
        } catch (err) {
            setError(errorText(err));
        }
    };
    const removeType = async (item) => {
        try {
            await deleteCompatType({ request, accountId, id: item.id });
            reloadMeta();
        } catch (err) {
            setError(errorText(err));
        }
    };
    const template = async () => {
        try {
            const blob = await downloadCompatTemplate({ fetchBlob, accountId });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'compat_template.xlsx';
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(errorText(err));
        }
    };
    const upload = async (file) => {
        if (!file) return;
        setError('');
        try {
            const result = await importCompat({ request, accountId, file });
            const problems = result.errors.slice(0, 3).map((item) => `строка ${item.row}: ${item.message}`).join('; ');
            setMessage(`Добавлено: ${result.created}, уже были: ${result.skipped}${result.errors_total ? `, с ошибками: ${result.errors_total} (${problems})` : ''}`);
            reload();
        } catch (err) {
            setError(errorText(err));
        } finally {
            if (fileRef.current) fileRef.current.value = '';
        }
    };
    const edit = (item) => setForm({
        id: item.id, type: item.type, bidirectional: item.bidirectional, note: item.note,
        from: { scope: item.from.scope, items: [{ key: item.from.key, label: item.from.label }] },
        to: { scope: item.to.scope, items: [{ key: item.to.key, label: item.to.label }] },
    });
    const customTypes = meta.types.filter((item) => item.custom);

    return (
        <div className="fb" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-card">
                <div className="fb-row fb-spread">
                    <div>
                        <h4 style={{ margin: 0 }}>Совместимости</h4>
                        <div className="fb-muted">
                            Связи между артикулами, моделями, брендами и категориями: аналог, запчасть, допродажа и другие.
                            Если покупатель спрашивает, с чем использовать или чем заменить, ИИ ответит по этим связкам и даст SKU и ссылку на товар в наличии.
                        </div>
                    </div>
                    <div className="fb-row" style={{ flexWrap: 'wrap' }}>
                        <input className="fb-input" placeholder="Поиск" value={query} onChange={(event) => { setQuery(event.target.value); setOffset(0); }} />
                        <button type="button" className="primary" onClick={() => setForm({})}>Добавить связку</button>
                        <button type="button" onClick={template}>Шаблон Excel</button>
                        <button type="button" onClick={() => fileRef.current && fileRef.current.click()}>Загрузить Excel</button>
                        <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={(event) => upload(event.target.files[0])} />
                    </div>
                </div>
                {message && <div className="fb-note" style={{ marginTop: 8 }}>{message}</div>}
            </div>
            {error && <div className="fb-card fb-chip--bad">{error}</div>}
            {form && (
                <LinkForm key={form.id || 'new'} accountId={accountId} meta={meta} initial={form.id ? form : null}
                    onSaved={() => { setForm(null); reload(); }} onCancel={() => setForm(null)} onMetaChanged={reloadMeta} />
            )}
            <div className="fb-table-wrap">
                <table className="fb-table">
                    <thead>
                        <tr><th>Откуда</th><th className="fb-num">Остаток</th><th>Тип</th><th>Куда</th><th className="fb-num">Остаток</th><th>Заметка</th><th /></tr>
                    </thead>
                    <tbody>
                        {data.items.map((item) => (
                            <tr key={item.id} className="fb-clickable" onClick={() => edit(item)}>
                                <td><SideCell side={item.from} /></td>
                                <td className="fb-num">{item.from.stock ?? '—'}</td>
                                <td><span className="fb-chip">{item.type_label} {item.bidirectional ? '↔' : '→'}</span></td>
                                <td><SideCell side={item.to} /></td>
                                <td className="fb-num">{item.to.stock ?? '—'}</td>
                                <td style={{ maxWidth: 260 }}>{item.note || ''}</td>
                                <td><button type="button" onClick={(event) => { event.stopPropagation(); remove(item.id); }}>Удалить</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {data.items.length === 0 && <div className="fb-empty">Связок пока нет. Добавьте первую или загрузите таблицей Excel.</div>}
            </div>
            {data.total > PAGE && (
                <div className="fb-row">
                    <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(offset - PAGE, 0))}>Назад</button>
                    <span className="fb-note">{offset + 1}–{Math.min(offset + PAGE, data.total)} из {data.total}</span>
                    <button type="button" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Дальше</button>
                </div>
            )}
            {customTypes.length > 0 && (
                <div className="fb-row" style={{ flexWrap: 'wrap' }}>
                    <span className="fb-muted">Свои типы:</span>
                    {customTypes.map((item) => (
                        <span key={item.key} className="fb-chip" title={item.hint}>
                            {item.label} <button type="button" aria-label="Удалить тип" onClick={() => removeType(item)}>✕</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
